import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";
import { Fragment } from "react";
import { useTranslations } from "use-intl";

import { usePaymentContext } from "@/app/lib/context/PaymentContext";
import {
  includeBnplFee,
  calculateMonthlyInstallment,
} from "@/app/lib/utils/bnpl-fee";
import { formatCurrency } from "@/app/lib/utils/currency";
import { Feature, isFeatureEnabled } from "@/app/lib/utils/featureFlags";
import { Image } from "@/app/ui/Image";
import { HotelRoomTitle } from "@/features/hotels/components/hotel/HotelRoomTitle";
import { useHotelBookingContext } from "@/features/hotels/contexts/HotelBookingContext";
import type {
  HotelBookingRoomOption,
  HotelOffer,
} from "@/features/hotels/types/types";

interface HotelRoomSelectionProps {
  offers: HotelOffer[];
  onRoomGalleryClick: (room: HotelBookingRoomOption) => void;
}

export const HotelRoomSelection = ({
  offers,
  onRoomGalleryClick,
}: HotelRoomSelectionProps) => {
  const { setData: setHotelBooking, data: hotelBooking } =
    useHotelBookingContext();

  const { maxAvailableInstallments, isEligible } = usePaymentContext();
  const isBnplFeatureEnabled =
    isFeatureEnabled(Feature.Bnpl) && Boolean(isEligible);
  const t = useTranslations();

  return (
    <div className="flex w-full flex-col px-4">
      {offers.map((offer, offerIndex) => (
        <Fragment key={offerIndex}>
          <div className="section-title">
            {t("hotels.search.option")} #{offerIndex + 1}
          </div>
          {offer.rooms.map((room, index) => (
            <div
              className="section-card flex flex-col justify-between gap-2 not-last-of-type:mb-2"
              key={index}
            >
              <div className="section-title m-0">
                {t("hotels.search.room")} {index + 1} (
                {t("hotels.search.selectOne")})
              </div>
              <FormControl>
                <RadioGroup
                  className="flex-column flex gap-4"
                  onChange={(e) => {
                    const isCurrentOffer =
                      offer.offerId === hotelBooking?.offerId &&
                      offer.packageToken === hotelBooking?.packageToken;
                    const rooms = isCurrentOffer ? hotelBooking.rooms : [];
                    const target = room.options.find(
                      (o) => o.roomToken === e.target.value,
                    )!;
                    rooms.splice(isCurrentOffer ? index : 0, 1, target);
                    setHotelBooking({
                      packageToken: offer.packageToken,
                      rooms,
                      offerId: offer.offerId,
                      price: target.price || offer.price,
                    });
                  }}
                >
                  {room.options.map((option) => {
                    const roomImage = option.images?.[0];
                    const price = option.price || offer.price;

                    return (
                      <div
                        className="mb-2 flex justify-between gap-4 rounded-2xl bg-[#F3F4F7] p-3 ps-1"
                        key={option.roomToken}
                      >
                        <FormControlLabel
                          name={option.roomToken}
                          value={option.roomToken}
                          control={<Radio />}
                          disabled={
                            offer.offerId !== hotelBooking?.offerId && index > 0
                          }
                          checked={
                            option.roomToken ===
                            hotelBooking?.rooms[index]?.roomToken
                          }
                          label={
                            <div>
                              <span className="font-medium">
                                <HotelRoomTitle
                                  name={option.name}
                                  board={option.board}
                                  occupancy={room.occupancy}
                                />
                              </span>
                              <div className="mt-2 text-base font-bold">
                                <span>
                                  {formatCurrency(
                                    price?.value,
                                    price?.currency,
                                  )}
                                </span>
                              </div>
                              {isBnplFeatureEnabled && (
                                <div className="flex flex-wrap items-end gap-1">
                                  <span className="text-xs font-medium text-text-tertiary">
                                    {t("orFrom")}
                                  </span>
                                  <span className="text-base leading-[20px] font-bold">
                                    {formatCurrency(
                                      calculateMonthlyInstallment(
                                        includeBnplFee(price?.value || 0),
                                        maxAvailableInstallments,
                                      ),
                                      price?.currency,
                                    )}
                                  </span>
                                  <span className="font-bold">
                                    {t("payment.perMonth")}
                                  </span>
                                </div>
                              )}
                            </div>
                          }
                          sx={{
                            m: 0,
                            "& .MuiTypography-root": {
                              fontSize: 14,
                              color: "var(--color-text-title)",
                            },
                          }}
                        />
                        {roomImage ? (
                          <button
                            type="button"
                            className="relative aspect-square h-full min-h-[48px] w-[60px] shrink-0 overflow-clip rounded-lg"
                            onClick={() => {
                              onRoomGalleryClick(option);
                            }}
                          >
                            <Image
                              className="h-full w-full object-cover"
                              src={roomImage.url}
                              alt={roomImage.name}
                            />

                            <div className="absolute bottom-0 left-0 z-1 bg-black ps-1.5 pe-1">
                              <div className="flex items-center justify-center gap-0.5">
                                <div className="text-xs text-white">
                                  {option.images?.length}+
                                </div>
                                <Image
                                  src={"/image.svg"}
                                  width={9}
                                  height={9}
                                  alt="image"
                                />
                              </div>
                            </div>
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </RadioGroup>
              </FormControl>
            </div>
          ))}
        </Fragment>
      ))}
    </div>
  );
};
